/**
 * Bhrigu Trimsamsha D30 Calculation Logic
 * Based on Maharishi Bhrigu teachings - 30 divisions for spiritual qualities and trials
 *
 * Structure:
 * - 30 Svami (Lords) - one for each degree (0°-30°)
 * - Grouped into 3 spiritual categories:
 *   - YAMA (Restraints) - degrees 1-10
 *   - NIYAMA (Observances) - degrees 11-20
 *   - BHAKTI (Devotion) - degrees 21-30
 *
 * Formula: Svami_number = floor(degrees_in_rashi) + 1
 */

import { JatakaResponse, GrahaPosition } from '../../../../../models/api';

export interface PlanetDisplay {
  name: string;
  short_name: string;
  longitude: number;
  is_retrograde: boolean;
  is_lagna?: boolean;
  is_hora?: boolean;
  is_chandra?: boolean;
}

/**
 * 30 Svami (Lord) types - spiritual qualities
 */
export const SVAMI_TYPES = [
  'SATYA', 'DAYA', 'TAPA', 'SHAUCA', 'TITIKSHA',
  'IKSHA', 'SHAMA', 'DAMA', 'AHIMSA', 'BRAHMACHARYA',
  'TYAGA', 'SVADHYAYA', 'ARJAVA', 'SANTOSHA', 'ATMAVASHA',
  'ASTEYA', 'SVAPNA_VIPARYAYA', 'MAUNA', 'ATMA_MARSHANA', 'DANA',
  'ASTIKYA', 'SHRAVANA', 'KIRTANA', 'SMARANA', 'MAHATMA_SAMSEVA',
  'IJYA', 'AVANATI', 'DASYA', 'SAKHYA', 'SA_ATMA_ARPANA'
] as const;

export type SvamiType = typeof SVAMI_TYPES[number];

/**
 * Spiritual categories
 */
export type SpiritualCategory = 'YAMA' | 'NIYAMA' | 'BHAKTI';

/**
 * Svami descriptions with transliteration, English, and spiritual meaning
 */
export const SVAMI_DESCRIPTIONS: Record<SvamiType, {
  transliteration: string;
  english: string;
  quality: string;
  category: SpiritualCategory;
}> = {
  SATYA: {
    transliteration: 'Satya',
    english: 'Truth',
    quality: 'Truthfulness, honesty',
    category: 'YAMA'
  },
  DAYA: {
    transliteration: 'Dayā',
    english: 'Compassion',
    quality: 'Mercy, kindness',
    category: 'YAMA'
  },
  TAPA: {
    transliteration: 'Tapa',
    english: 'Austerity',
    quality: 'Voluntary self-restraint, penance',
    category: 'YAMA'
  },
  SHAUCA: {
    transliteration: 'Śauca',
    english: 'Purity',
    quality: 'Cleanliness (external & internal)',
    category: 'YAMA'
  },
  TITIKSHA: {
    transliteration: 'Titikṣā',
    english: 'Patience',
    quality: 'Forbearance, endurance',
    category: 'YAMA'
  },
  IKSHA: {
    transliteration: 'Īkṣā',
    english: 'Observation',
    quality: 'Contemplation, discernment',
    category: 'YAMA'
  },
  SHAMA: {
    transliteration: 'Śama',
    english: 'Tranquility',
    quality: 'Calmness of mind, serenity',
    category: 'YAMA'
  },
  DAMA: {
    transliteration: 'Dama',
    english: 'Self-control',
    quality: 'Restraint, self-mastery',
    category: 'YAMA'
  },
  AHIMSA: {
    transliteration: 'Ahiṃsā',
    english: 'Non-violence',
    quality: 'Non-injury, harmlessness',
    category: 'YAMA'
  },
  BRAHMACHARYA: {
    transliteration: 'Brahmacārya',
    english: 'Celibacy',
    quality: 'Energy conservation, chastity',
    category: 'YAMA'
  },
  TYAGA: {
    transliteration: 'Tyāga',
    english: 'Renunciation',
    quality: 'Sacrifice, letting go',
    category: 'NIYAMA'
  },
  SVADHYAYA: {
    transliteration: 'Svādhyāya',
    english: 'Self-study',
    quality: 'Study of sacred texts',
    category: 'NIYAMA'
  },
  ARJAVA: {
    transliteration: 'Ārjava',
    english: 'Sincerity',
    quality: 'Straightforwardness, openness',
    category: 'NIYAMA'
  },
  SANTOSHA: {
    transliteration: 'Santoṣa',
    english: 'Contentment',
    quality: 'Satisfaction, gentleness',
    category: 'NIYAMA'
  },
  ATMAVASHA: {
    transliteration: 'Ātmavaśa',
    english: 'Self-mastery',
    quality: 'Strong will, firm intentions',
    category: 'NIYAMA'
  },
  ASTEYA: {
    transliteration: 'Asteya',
    english: 'Non-stealing',
    quality: 'Not appropriating, honesty',
    category: 'NIYAMA'
  },
  SVAPNA_VIPARYAYA: {
    transliteration: 'Svapna-viparyaya',
    english: 'Awakening',
    quality: 'Waking from the dream of illusion',
    category: 'NIYAMA'
  },
  MAUNA: {
    transliteration: 'Mauna',
    english: 'Silence',
    quality: 'Keeping silence, quietude',
    category: 'NIYAMA'
  },
  ATMA_MARSHANA: {
    transliteration: 'Ātma-marśana',
    english: 'Self-inquiry',
    quality: 'Soul investigation, introspection',
    category: 'NIYAMA'
  },
  DANA: {
    transliteration: 'Dāna',
    english: 'Charity',
    quality: 'Giving, generosity',
    category: 'NIYAMA'
  },
  ASTIKYA: {
    transliteration: 'Āstikya',
    english: 'Faith',
    quality: 'Devotion, piety',
    category: 'BHAKTI'
  },
  SHRAVANA: {
    transliteration: 'Śravaṇa',
    english: 'Hearing',
    quality: 'Listening to spiritual teachings',
    category: 'BHAKTI'
  },
  KIRTANA: {
    transliteration: 'Kīrtana',
    english: 'Chanting',
    quality: 'Glorification, singing praises',
    category: 'BHAKTI'
  },
  SMARANA: {
    transliteration: 'Smaraṇa',
    english: 'Remembrance',
    quality: 'Constant remembering of Divine',
    category: 'BHAKTI'
  },
  MAHATMA_SAMSEVA: {
    transliteration: 'Mahātma-saṃseva',
    english: 'Saintly association',
    quality: 'Approaching the Great Soul',
    category: 'BHAKTI'
  },
  IJYA: {
    transliteration: 'Ijyā',
    english: 'Worship',
    quality: 'Reverence, veneration',
    category: 'BHAKTI'
  },
  AVANATI: {
    transliteration: 'Avanati',
    english: 'Prostration',
    quality: 'Bowing down, obeisance',
    category: 'BHAKTI'
  },
  DASYA: {
    transliteration: 'Dāsya',
    english: 'Servitude',
    quality: 'Service, serving attitude',
    category: 'BHAKTI'
  },
  SAKHYA: {
    transliteration: 'Sakhya',
    english: 'Friendship',
    quality: 'Companionship with Divine',
    category: 'BHAKTI'
  },
  SA_ATMA_ARPANA: {
    transliteration: 'Sa-ātma-arpaṇa',
    english: 'Self-surrender',
    quality: 'Complete offering of self',
    category: 'BHAKTI'
  }
};

/**
 * Category descriptions
 */
export const CATEGORY_DESCRIPTIONS: Record<SpiritualCategory, string> = {
  YAMA: 'Restraints - Self-control and ethical foundations',
  NIYAMA: 'Observances - Positive practices and disciplines',
  BHAKTI: 'Devotion - Devotional service and surrender'
};

/**
 * Calculate Svami number from degrees in rashi (0-30)
 */
export function calculateSvamiNumber(degreeInRashi: number): number {
  // Each Svami = 1° (one degree)
  // Svami_number = floor(degrees) + 1
  const svamiIndex = Math.floor(degreeInRashi);
  return Math.min(svamiIndex + 1, 30);
}

/**
 * Get Svami type from Svami number (1-30)
 */
export function getSvamiType(svamiNumber: number): SvamiType {
  return SVAMI_TYPES[svamiNumber - 1] || SVAMI_TYPES[0];
}

/**
 * Get spiritual category from Svami number
 */
export function getSpiritualCategory(svamiNumber: number): SpiritualCategory {
  if (svamiNumber >= 1 && svamiNumber <= 10) {
    return 'YAMA';
  } else if (svamiNumber >= 11 && svamiNumber <= 20) {
    return 'NIYAMA';
  } else {
    return 'BHAKTI';
  }
}

/**
 * Single planet's Bhrigu Trimsamsha attributes
 */
export interface BhriguTrimsamshaAttributes {
  planet: PlanetDisplay;
  svamiNumber: number;
  svamiType: SvamiType;
  category: SpiritualCategory;
}

/**
 * Complete Bhrigu Trimsamsha data for all planets
 */
export interface BhriguTrimsamshaData {
  divisions: BhriguTrimsamshaAttributes[];
}

/**
 * Parse longitude from position string format "rashi|degrees|longitude|speed"
 */
export function parseLongitudeFromPosition(position: string): number {
  if (!position) {
    return 0;
  }

  const parts = position.split('|');
  if (parts.length >= 3) {
    return parseFloat(parts[2]);
  }

  return 0;
}

/**
 * Convert API planet to PlanetDisplay format
 */
export function convertToPlanetDisplay(planet: GrahaPosition, currentHoraPlanet?: string): PlanetDisplay {
  const longitude = parseLongitudeFromPosition(planet.position || '');

  const isHora = currentHoraPlanet && (
    planet.short_name === currentHoraPlanet ||
    planet.name === currentHoraPlanet
  );

  return {
    name: planet.name,
    short_name: planet.short_name,
    longitude: longitude,
    is_retrograde: planet.is_retrograde || false,
    is_lagna: planet.short_name === 'Lg',
    is_hora: isHora || false,
    is_chandra: planet.short_name === 'Ch' || planet.name === 'Chandra'
  };
}

/**
 * Calculate Bhrigu Trimsamsha for all planets
 */
export function calculateBhriguTrimsamshas(apiResponse: JatakaResponse): BhriguTrimsamshaData {
  const divisions: BhriguTrimsamshaAttributes[] = [];

  if (!apiResponse.graha_positions?.planets) {
    return { divisions };
  }

  // Get current hora planet for highlighting
  const currentHoraPlanet = apiResponse.detailed_panchanga?.hora_details?.current_planet;

  apiResponse.graha_positions.planets.forEach(planet => {
    // Convert to PlanetDisplay format
    const planetDisplay = convertToPlanetDisplay(planet, currentHoraPlanet);

    // Calculate degree within current rashi (0-30)
    const degreeInRashi = planetDisplay.longitude % 30;

    // Calculate Svami number (1-30)
    const svamiNumber = calculateSvamiNumber(degreeInRashi);

    // Get Svami type
    const svamiType = getSvamiType(svamiNumber);

    // Get spiritual category
    const category = getSpiritualCategory(svamiNumber);

    divisions.push({
      planet: planetDisplay,
      svamiNumber,
      svamiType,
      category
    });
  });

  return { divisions };
}
